import type React from 'react';
import { useMemo, useState } from 'react';

import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import SearchIcon from 'assets/icons/icn _ 16px _ Search.svg';
import LinkButton from 'components/buttons/LinkButton';
import TMXInput from 'components/inputs/TMXInput';
import { type IdNameParticipantsInterface, type PairedParticipantInterface } from 'components/overlays/doubles/types';
import { useStyles } from 'components/panels/doubles/styles';
import UnpairedPlayersListItem from 'components/panels/doubles/UnpairedPlayersListItem';
import { Feature, featureEnabled } from 'config/clients';

interface UnpairedPlayersPanelProps {
  unpairedParticipants?: IdNameParticipantsInterface[];
  setPairedParticipants: (pairedParticipants: (pairedParticipants) => PairedParticipantInterface[]) => void;
  setUnpairedParticipants: (unpairedParticipants: IdNameParticipantsInterface[]) => void;
}

const UnpairedPlayersPanel: React.FC<React.PropsWithChildren<UnpairedPlayersPanelProps>> = ({
  unpairedParticipants,
  setPairedParticipants,
  setUnpairedParticipants,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [allEntriesMode, setAllEntriesMode] = useState(false);
  const [filterParticipantsValue, setFilterParticipantsValue] = useState<string>('');

  const filteredParticipants = useMemo(() => {
    return unpairedParticipants.filter((participant) => {
      if (!allEntriesMode && participant.isPaired) return false;
      return participant?.name?.toLowerCase().includes(filterParticipantsValue?.toLowerCase());
    });
  }, [unpairedParticipants, allEntriesMode, filterParticipantsValue]);

  const handleOnChangeFilterParticipants = (event) => {
    setFilterParticipantsValue(event.target.value);
  };

  const handlePairParticipants = (participant) => {
    const newUnpairedParticipants = unpairedParticipants.filter(
      (unpairedParticipant) => unpairedParticipant.participantId !== participant.participantId,
    );
    setUnpairedParticipants(newUnpairedParticipants);
    setPairedParticipants((pairedParticipants) => {
      const ifPairedParticipants = pairedParticipants || [];
      const waitingForPair = ifPairedParticipants?.find(
        (pairedParticipant) => pairedParticipant.individualParticipantsIds.length === 1,
      );
      const newPairedParticipants = waitingForPair
        ? ifPairedParticipants?.filter(
            (ifPairedParticipants) =>
              ifPairedParticipants.individualParticipantsIds[0] !== waitingForPair.individualParticipantsIds[0],
          )
        : [
            ...ifPairedParticipants,
            {
              participantType: 'PAIR',
              participantRole: 'COMPETITOR',
              individualParticipantsIds: [participant.participantId],
              individualParticipants: [participant],
            },
          ];
      if (waitingForPair) {
        newPairedParticipants.push({
          participantType: 'PAIR',
          participantRole: 'COMPETITOR',
          individualParticipantsIds: [waitingForPair.individualParticipantsIds[0], participant.participantId],
          individualParticipants: [waitingForPair.individualParticipants[0], participant],
        });
      }
      return newPairedParticipants;
    });
  };

  return (
    <Grid container direction="column" justifyContent="center">
      <Grid item className={classes.createDoublesTitle}>
        <Typography className={classes.titleTypography} variant="h1">
          {allEntriesMode ? t('allEntries') : t('unpairedPlayers')} ({filteredParticipants?.length || 0})
        </Typography>
        {featureEnabled(Feature.MULTI_PAIRED_PARTICIPANTS) && (
          <LinkButton className={classes.showAllEntriesToggle} onClick={() => setAllEntriesMode(!allEntriesMode)}>
            {allEntriesMode ? t('showUnpaired') : t('showAllEntries')}
          </LinkButton>
        )}
      </Grid>
      <Grid item className={classes.unpairedPlayersPanelWrapper}>
        {unpairedParticipants?.length && unpairedParticipants.length > 0 ? (
          <Grid container direction="column">
            <Grid item className={classes.filterPlayersWrapper}>
              <TMXInput
                fullWidth
                placeholder={t('searchPlayers')}
                startAdornment={
                  <InputAdornment position="end">
                    <Tooltip aria-label={t('search')} title={t('search')}>
                      <IconButton className={classes.filterPlayersInputAdornment} size="large">
                        <img alt="search-icon" src={SearchIcon} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                }
                onChange={handleOnChangeFilterParticipants}
              />
            </Grid>
            <Grid item>
              <List className={classes.unpairedListItemsWrapper}>
                {filteredParticipants?.map((participant, index) => (
                  <UnpairedPlayersListItem
                    key={participant.participantId}
                    isLastItem={index + 1 === filteredParticipants.length}
                    participant={participant}
                    onPairParticipants={handlePairParticipants}
                  />
                ))}
              </List>
            </Grid>
          </Grid>
        ) : (
          <Grid item className={classes.emptyListWrapper}>
            <div className={classes.emptyListTextWrapper}>
              <Typography>{t('unpairedPlayersPanel.listWrapper')}</Typography>
            </div>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default UnpairedPlayersPanel;
